import { useEffect, useRef } from "react";
import { SearchIcon, CloseIcon } from "../../components/Icons";

// Search on /work — the last chip in the row, always at its right edge.
//
// The same markup in both states. Nothing is swapped, faded or re-rendered when
// it opens: the icon, the input and the X are always present, and the only
// thing that changes is how wide the box around them is. Everything past that
// width is clipped.
//
// Which order things are lost in is flex priority, not logic. The input gives up
// its slack first; once it reaches its floor the X is pushed past the right edge
// and cut; the icon and placeholder hold longest, because the icon is the one
// part that has to survive. Reading the collapse backwards gives the expansion.
//
// Icon-only when closed, and not for want of discoverability — nobody searches a
// portfolio cold, since you cannot look for work you have never seen. It is for
// someone coming back for something they half-remember, which is also why the
// matching tolerates a misremembered spelling.
function WorkSearch({ open, value, onChange, onOpen, onClose, count }) {
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <div className="relative w-full rounded-full border border-border-light dark:border-border-dark bg-white dark:bg-black overflow-hidden">
      {/* Horizontal padding is what makes the closed box square at chip height:
          14px of icon between 6px either side on mobile, 10px at sm. */}
      <div className="flex items-center gap-2 py-1 sm:py-1.5 px-[6px] sm:px-[10px]">
        {/* Matches a chip's text line-box, so this sits level with its
            neighbours rather than riding low. */}
        <span className="flex-none flex items-center h-4 sm:h-5 text-text-muted-light dark:text-text-muted-dark">
          <SearchIcon />
        </span>

        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Search"
          aria-label="Search"
          tabIndex={open ? 0 : -1}
          // The floor is what makes the X go before the placeholder does:
          // without it the input would shrink to nothing first.
          className="flex-1 min-w-[5rem] bg-transparent outline-none font-sans text-xs sm:text-sm text-text-primary-light dark:text-text-primary-dark placeholder:text-text-muted-light dark:placeholder:text-text-muted-dark"
        />

        {value.trim() && (
          <span className="flex-none font-sans text-xs text-text-muted-light dark:text-text-muted-dark">
            {count}
          </span>
        )}

        <button
          onClick={onClose}
          aria-label="Close search"
          tabIndex={open ? 0 : -1}
          className="flex-none flex items-center justify-center h-4 sm:h-5 text-text-muted-light dark:text-text-muted-dark hover:text-text-primary-light dark:hover:text-text-primary-dark transition-colors"
        >
          <CloseIcon />
        </button>
      </div>

      {/* Closed, the input fills most of the square, so the whole chip needs to
          be the hit target rather than the icon alone. */}
      {!open && (
        <button
          onClick={onOpen}
          aria-label="Search projects"
          aria-expanded={false}
          className="absolute inset-0 cursor-pointer"
        />
      )}
    </div>
  );
}

export default WorkSearch;
